import React from 'react';
import {useForm} from "react-hook-form";
import {Require} from "../Require";
import {NavLink, useNavigate} from "react-router-dom";
import {race} from "../../../models/race";
import {scrollTo} from "../../../service/ToolsService";
import {ApiError, ApiService} from "../../../service/ApiService";
import {raceResultType} from "../../../types/Api/RaceResultType";
import DateService from "../../../service/DateService";

enum ParticipationEnum {
  nothing = 'nothing',
  paid = 'paid',
  subsidized = 'subsidized',
}

type FormDataType = {
  label: string,
  description?: string,
  rideDate: string,
  ranking: string,
  participation: ParticipationEnum,
  startRegistration?: string,
  endRegistration?: string,
  participantLimit?: number,
  confirm?: boolean,
  link?: string,
  seasonId: string,
}

type RaceFormProps = {
  race?: race,
  seasonId?: string,
}

export const RaceForm = ({race, seasonId}: RaceFormProps) => {
  const pattern_link = /^(https?:\/\/)?([a-zA-Z0-9.-]+)\.([a-zA-Z]{2,})(:[0-9]{1,5})?(\/[^\s]*)?$/;
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: {errors}
  } = useForm<FormDataType>({
    defaultValues: {
      label: race?.label ?? '',
      description: race?.description ?? '',
      participation: race?.paid ? ParticipationEnum.paid : race?.subsidized ? ParticipationEnum.subsidized : ParticipationEnum.nothing,
      link: race?.link ?? '',
      ranking: race?.ranking === true ? 'true' : race?.ranking === false ? 'false' : '',
      confirm: !(race?.cancel ?? false),
      seasonId: race?.season?.seasonId ?? seasonId ?? '',
      participantLimit: race?.participantLimit ?? undefined,
      rideDate: race?.rideDate ? DateService.getDate(race.rideDate) : '',
      startRegistration: race?.startRegistration ? DateService.getDate(race.startRegistration) : '',
      endRegistration: race?.endRegistration ? DateService.getDate(race.endRegistration) : '',
    }
  });

  const onSubmit = async (data: FormDataType) => {
    const d = {
      label: data.label,
      description: data.description,
      cancel: !data.confirm,
      ranking: data.ranking === 'true',
      paid: data.participation === ParticipationEnum.paid,
      subsidized: data.participation === ParticipationEnum.subsidized,
      rideDate: data.rideDate,
      startRegistration: !data.startRegistration ? null : data.startRegistration,
      endRegistration: !data.endRegistration ? null : data.endRegistration,
      link: !data.link ? null : data.link,
      participantLimit: !data.participantLimit ? null : data.participantLimit,
      seasonId: seasonId,
    };

    try {
      const result = race
        ? await ApiService.patch(`/race/${race.raceId}`, {param: () => d}) as raceResultType
        : await ApiService.post('/race', {param: () => d}) as raceResultType;

      navigate(result.season
        ? `/admin/season/show/${result.season?.seasonId}`
        : `/admin/season`
      );
      scrollTo();
    } catch (e: any) {
      const error: ApiError = e as ApiError;

      error.errors?.forEach((e: {
        field: string,
        message: string
      }) => {
        setError(e.field as "label", {
          type: "manual",
          message: e.message
        })
      });
    }
  }

  return (
    <>
      <form className="p-3" method="post" onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex">
          <div className="col max-w600px pe-4">
            <div className="form-check form-switch mb-3">
              <input className="form-check-input" type="checkbox" id="form-confirm" {...register('confirm')} />
              <label className="form-check-label" htmlFor="form-confirm">
                {`${watch('confirm') ? 'La sortie est programmée' : 'La sortie est annulée'}`}
              </label>
            </div>

            <div className="mb-3">
              <label className="form-label">Type de sortie <Require/></label>
              <div className="d-flex">
                <div className="form-check me-3">
                  <input className="form-check-input" type="radio" id="form-ranking-1" value="true" {...register('ranking', {
                    required: 'Vous devez choisir le type de sortie',
                  })}/>
                  <label className="form-check-label" htmlFor="form-ranking-1">
                    Course
                  </label>
                </div>
                <div className="form-check me-3">
                  <input className="form-check-input" type="radio" id="form-ranking-2" value="false" {...register('ranking', {
                    required: 'Vous devez choisir le type de sortie',
                  })}/>
                  <label className="form-check-label" htmlFor="form-ranking-2">
                    Randonnée
                  </label>
                </div>
              </div>
              {errors.ranking && <p className="text-danger">{errors.ranking.message}</p>}
            </div>

            <div className="mb-3">
              <label className="form-label">Participation du club <Require/></label>
              <div className="d-flex">
                <div className="form-check me-3">
                  <input className="form-check-input" type="radio" id="form-participation-paid" value={ParticipationEnum.paid} {...register('participation', {
                    required: 'Vous devez choisir la participation du club',
                  })} />
                  <label className="form-check-label" htmlFor="form-participation-paid">
                    Payée
                  </label>
                </div>
                <div className="form-check me-3">
                  <input className="form-check-input" type="radio" id="form-participation-subsidized" value={ParticipationEnum.subsidized} {...register('participation', {
                    required: 'Vous devez choisir la participation du club',
                  })} />
                  <label className="form-check-label" htmlFor="form-participation-subsidized">
                    Subventionnée
                  </label>
                </div>
                <div className="form-check me-3">
                  <input className="form-check-input" type="radio" id="form-participation-nothing" value={ParticipationEnum.nothing} {...register('participation', {
                    required: 'Vous devez choisir la participation du club',
                  })} />
                  <label className="form-check-label" htmlFor="form-participation-nothing">
                    Sans participation
                  </label>
                </div>
              </div>
              {errors.participation && <p className="text-danger">{errors.participation.message}</p>}
            </div>

            <div className="mb-3 d-flex justify-content-between">
              <div className="col max-w200px me-1">
                <label className="form-label" htmlFor="form-ride-date">Date de la sortie <Require/></label>

                <input type="date" className="form-control" id="form-ride-date" {...register('rideDate', {
                  required: 'La date de la sortie est obligatoire',
                })}
                />
                {errors.rideDate && <p className="text-danger">{errors.rideDate.message}</p>}
              </div>
              <div className="col max-w200px ms-1">
                <label className="form-label" htmlFor="form-participationlimit">Max participant</label>
                <input id="form-participationlimit" type="number" className="form-control" placeholder="~" {...register('participantLimit', {
                  setValueAs: (value) => (value === "" ? undefined : Number(value)),
                  validate: {
                    valide: (value) =>
                      value === undefined || Number.isInteger(value) || "Le nombre doit être un entier",
                  },
                })} />
                {errors.participantLimit && <p className="text-danger">{errors.participantLimit.message}</p>}
              </div>
            </div>

            <div className="mb-3 d-flex justify-content-between">
              <div className="col max-w200px me-1">
                <label className="form-label" htmlFor="form-startregistration">Début d'inscription</label>
                <input type="date" className="form-control" id="form-startregistration" {...register('startRegistration')} />
                {errors.startRegistration && <p className="text-danger">{errors.startRegistration.message}</p>}
              </div>
              <div className="col max-w200px ms-1">
                <label className="form-label" htmlFor="form-endregistration">Fin d'inscription</label>

                <input type="date" className="form-control" id="form-endregistration" {...register('endRegistration')} />
                {errors.endRegistration && <p className="text-danger">{errors.endRegistration.message}</p>}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="form-label">Nom <Require/></label>
              <input id="form-label" type="text" className="form-control" placeholder="~" {...register('label', {
                required: 'Le nom est obligatoire',
                validate: {
                  minLength: (value) =>
                    (value ?? '').length <= 255 || 'Le nom ne doit pas dépasser 255 caractères',
                },
              })} />
              {errors.label && <p className="text-danger">{errors.label.message}</p>}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="form-description">Description</label>
              <textarea id='form-description' className="form-control" rows={10} cols={50} placeholder="~" {...register('description')} />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="form-link">Lien officiel</label>
              <input id="form-link" type="text" className="form-control" placeholder="~" {...register('link', {
                validate: {
                  format: (value) =>
                    (value ? pattern_link.test(value) : true) || 'Doit être une adresse internet valide',
                },
              })} />
              {errors.link && <p className="text-danger">{errors.link.message}</p>}
            </div>
          </div>
        </div>

        <div className="mt-3">
          <button className="btn btn-sm btn-success me-1" type="submit">Enregistrer</button>
          {!seasonId && (
            <NavLink to={`/admin/season`} onClick={scrollTo} className="btn btn-sm btn-info me-1">
              Retour aux saisons
            </NavLink>
          )}
          {seasonId && (
            <NavLink to={`/admin/season/show/${seasonId}`} onClick={scrollTo} className="btn btn-sm btn-info me-1">
              Retour
            </NavLink>
          )}
        </div>
      </form>
    </>
  )
}
